import React, { useContext, useState, useEffect, useRef } from 'react';
import { Col, Row, Form, Button, Container, Card, InputGroup } from 'react-bootstrap'
import 'bootstrap/dist/css/bootstrap.min.css';
import { schema } from '../../schema';
import { Formik, FieldArray, getIn, FormikProps, setNestedObjectValues } from 'formik';
import ColorPicker from '../CustomFormHandling/colorPicker'
import "../../optional"; // custom "optional" function for yup validation
import modelContext from '../../modelContext';
import { annotSpecies } from '../../types';
import { SpeciesForm } from './species_types';
import { CustomSelect } from '../CustomFormHandling/CustomSelect';


export default function FormSpecies(props: any) {
	const [submitSuccess, setSubmitSuccess] = useState(false);
	const [touchedSubmit, setTouchedSubmit] = useState(false);
	useEffect(() => { }, [submitSuccess])
	useEffect(() => { }, [touchedSubmit])


	let emptySpecies = {
		_attributes: {
			id: "",
			name: "",
			compartment: "default"
		},
		annotation: {
			"cell4d:listOfValidCompartments": {
				"cell4d:compartment": [
					{
						_attributes: {
							id: "",
							initial: "0",
							distribution: "random",
							xPoint: "",
							yPoint: "",
							zPoint: "",
							rate: ""
						}
					}
				]
			},
			"cell4d:listOfSpeciesTypes": {
				"cell4d:speciesType": []
			}
		},
	}
	let empty_speciesType = {
		_attributes: {
			id: ""
		},
		"cell4d:bindingSite": []
	}

	const model = useContext(modelContext);
	// formik reference
	const formRef = useRef<FormikProps<SpeciesForm>>(null);
	const [initialValues, setInitialValues] = useState(props.initialValues);

	// This function handles overwriting existing forms by uploading a new form. it clears the existing one first, then replaces the blank from with new data
	// the setTimeout is a workaround because you cannot listen for changes made to setFieldValue, which is async.
	// the overwrite is needed because the checkboxes that toggle membrane field options can be stale when updated from upload, so everything needs to be cleared.
	// fix in the future if possible, but no big deal
	const handleFieldOverwrite = () => {
		if (formRef?.current) {
			formRef?.current.setFieldValue(`species`, []);
			setTimeout(() => {
				formRef?.current?.setFieldValue(`species`, model.modelJson["listOfSpecies"]["species"]);
			}, 100)
		}
	}
	useEffect(() => {
		setInitialValues(props.initialValues);
		handleFieldOverwrite();
	}, [props.initialValues])

	const isFirstRun = useRef(true);

	const [bindSiteState, setBindSiteState] = useState(false);
	const [bindSites, setBindSites] = useState<Array<Array<annotSpecies>>>([]);

	// produces an array of empty bind sites with id filled for an annot species
	function fillBindSites(annot_id: string): Array<any> {
		let annotSpecies = model.annotSpeciesList[annot_id];
		let bindSiteArray = Array(annotSpecies?.sites?.length).fill(0).map(() => ({
			_attributes: {
				id: "",
				state: "",
				binding: ""
			}
		}));
		if (!annotSpecies?.sites?.length) {
			bindSiteArray = [];
		} else {
			Object.values(annotSpecies?.sites).forEach((site: any, index: number) => {
				bindSiteArray[index]._attributes.id = site.id;
			});
		}
		return (bindSiteArray);
	}

	useEffect(() => {
		if (isFirstRun.current) {
			isFirstRun.current = false;
			return;
		}
		if (formRef?.current?.values) {
			setBindSites(formRef.current.values["species"].map((species: any, index: number) => {
				let annotSpeciesBindArray = species["annotation"]["cell4d:listOfSpeciesTypes"]["cell4d:speciesType"].map((annotSpecies: any, index: number) => {
					let annot_id = annotSpecies._attributes.id;
					return (fillBindSites(annot_id));
				})
				return annotSpeciesBindArray;
			}))
		}

	}, [bindSiteState, model.modelJson])

	useEffect(() => {
		if (formRef?.current) {
			if (formRef.current.values["species"].length !== 0) {
				formRef.current.validateForm();
				setTimeout(() => {
					formRef?.current?.setTouched(setNestedObjectValues(formRef.current.errors, true));
				}, 100)
			}
		}

	}, [model.compartList, model.annotSpeciesList])

	const errorsMap = (error_string: string): string => {
		let return_string = "";
		if (error_string === "missing_var") {
			return_string = "This field cannot be empty.";
		} else if (error_string === "invalid_var") {
			return_string = "This field is invalid.";
		} else if (error_string === "no_match") {
			return_string = "The highlighted option is no longer valid. Please select a new option.";
		}
		return (return_string);
	}

	const handleSubmit = (values: any) => {
		let species_out = {
			"species": values["species"]
		}

		// Check to see if the submitted species list will break the existing model. if so, create warning. if ignored, set broken tabs to false, disable save model until fixed.
		let temp_speciesList: Array<string> = values.species.map((species: any) => {
			let id: string = species._attributes.id;
			return (id)
		});
		// validateModel input order is comparts, annot, metabs, species
		let temp_modelStatus: Array<Boolean> = model.validateModel(undefined, undefined, undefined, temp_speciesList);
		// because handleSubmit was successfully called, new species tab is correct for sure, so set it to true.
		temp_modelStatus[3] = true;

		if (!temp_modelStatus.every(tab => tab === true)) {
			let error_string = "";
			if (!temp_modelStatus[5]) error_string += "\r\nEvents";
			let confirmed = window.confirm("This new compartment list will introduce errors in the following tabs: " + error_string +
				"\r\nAre you sure? Save model button will be disabled until all model tabs are valid again.");
			if (!confirmed) return;
		}
		model.setModelStatus(temp_modelStatus);

		model.appendModelJson("listOfSpecies", species_out);
		model.updateSpecies(values.species);
		// console.log(JSON.stringify(values["species"]));

		setSubmitSuccess(true);
		setTouchedSubmit(false);
	}

	return (
		<div>
			<Formik
				innerRef={formRef}
				validationSchema={schema.species(model.compartList, Object.values(model.annotSpeciesList))}
				onSubmit={handleSubmit}
				enableReinitialize={false}
				initialValues={initialValues}
			>
				{({
					handleSubmit,
					handleChange,
					handleBlur,
					values,
					touched,
					errors,
					setFieldValue
				}) => (
					<Form onSubmit={handleSubmit} noValidate autoComplete="off">
						{/* <pre>{JSON.stringify(model.annotSpeciesList, null, 2)}</pre> */}
						<Container>
							<FieldArray name="species" render={arrayHelpers => {
								return (<div> {
									values?.["species"] && values?.["species"].length > 0 ? values["species"].map((species: any, index: number) => (
										<div key={index}>
											<Card >
												{/* Molecule ID, compartment, base species */}
												<Row style={{ paddingTop: 10 }}>
													<Col xs={3} style={{ paddingLeft: 30 }}>
														<Form.Label>Molecule name</Form.Label>
														<Form.Control className="form-control" size="sm"
															name={`species.[${index}]._attributes.id`}
															value={values["species"][index]._attributes.id}
															onChange={handleChange}
															onBlur={handleBlur}
															isValid={getIn(touched, `species.[${index}]._attributes.id`) &&
																!getIn(errors, `species.[${index}]._attributes.id`)}
															isInvalid={getIn(touched, `species.[${index}]._attributes.id`) &&
																!!getIn(errors, `species.[${index}]._attributes.id`)} />
														<Form.Control.Feedback type="invalid">{errorsMap(getIn(errors, `species.[${index}]._attributes.id`))}</Form.Control.Feedback>
														<br />

														{/* optional diffusion and color display */}
														{values["species"][index]["annotation"]["cell4d:diffusionConstant"] ?
															<>
																<Form.Label>Diffusion constant</Form.Label>
																<InputGroup size="sm">
																	<Form.Control type="number" className="form-control" size="sm"
																		name={`species.[${index}]["annotation"][cell4d:diffusionConstant]._attributes.value`}
																		value={values["species"][index]["annotation"]["cell4d:diffusionConstant"]._attributes.value}
																		onChange={handleChange}
																		onBlur={handleBlur}
																		isValid={getIn(touched, `species.[${index}]["annotation"][cell4d:diffusionConstant]._attributes.value`) &&
																			!getIn(errors, `species.[${index}]["annotation"][cell4d:diffusionConstant]._attributes.value`)}
																		isInvalid={getIn(touched, `species.[${index}]["annotation"][cell4d:diffusionConstant]._attributes.value`) &&
																			!!getIn(errors, `species.[${index}]["annotation"][cell4d:diffusionConstant]._attributes.value`)} />
																	<InputGroup.Append>
																		<InputGroup.Text id="diff_const">m<sup>2</sup>/s</InputGroup.Text>
																	</InputGroup.Append>
																	<Form.Control.Feedback type="invalid">{errorsMap(getIn(errors, `species.[${index}]["annotation"][cell4d:diffusionConstant]._attributes.value`))}</Form.Control.Feedback>
																</InputGroup>
																<Button type="button" size="sm" variant="outline-danger" onClick={() => {
																	setFieldValue(`species.[${index}]["annotation"][cell4d:diffusionConstant]`, undefined)
																}}>Remove custom diffusion constant</Button>
															</>
															:
															<Button className="align-middle"
																type="button"
																size="sm"
																variant="outline-secondary"
																onClick={() => {
																	setFieldValue(`species.[${index}]["annotation"][cell4d:diffusionConstant]`, { _attributes: { value: 0 } })
																}}>
																Add custom diffusion constant
															</Button>
														}
														<br />
														{values["species"][index]["annotation"]["cell4d:displayProperties"] ?
															<>
																<br />
																<Form.Label>Custom color</Form.Label>
																<ColorPicker onChange={(color: { rgb: any }) => {
																	setFieldValue(`species.[${index}]["annotation"]["cell4d:displayProperties"]._attributes.redValue`, color.rgb.r)
																	setFieldValue(`species.[${index}]["annotation"]["cell4d:displayProperties"]._attributes.greenValue`, color.rgb.g)
																	setFieldValue(`species.[${index}]["annotation"]["cell4d:displayProperties"]._attributes.blueValue`, color.rgb.b)
																}}
																	red={values["species"][index]["annotation"]["cell4d:displayProperties"]._attributes.redValue}
																	green={values["species"][index]["annotation"]["cell4d:displayProperties"]._attributes.greenValue}
																	blue={values["species"][index]["annotation"]["cell4d:displayProperties"]._attributes.blueValue}
																/>
																<Button type="button" size="sm" variant="outline-danger" onClick={() => {
																	setFieldValue(`species.[${index}]["annotation"]["cell4d:displayProperties"]`, undefined)
																}}>Remove custom color</Button>
															</>
															:
															<>
																<br />
																<Button className="align-middle"
																	type="button"
																	size="sm"
																	variant="outline-secondary"
																	onClick={() => {
																		setFieldValue(`species.[${index}]["annotation"]["cell4d:displayProperties"]`, { _attributes: { redValue: 0, greenValue: 0, blueValue: 0 } })
																	}}>
																	Add custom color
																</Button>
																<br />
																<br />
															</>
														}
													</Col>
													<Col style={{ paddingLeft: 0 }} xs={4}>
														<FieldArray name={`species.[${index}]["annotation"]["cell4d:listOfValidCompartments"]["cell4d:compartment"]`} render={arrayHelpers => {
															return (<div> {
																values?.["species"]?.[index]?.["annotation"]?.["cell4d:listOfValidCompartments"]?.["cell4d:compartment"] && values?.["species"]?.[index]?.["annotation"]?.["cell4d:listOfValidCompartments"]?.["cell4d:compartment"].length > 0 ?
																	values["species"][index]["annotation"]["cell4d:listOfValidCompartments"]["cell4d:compartment"].map((compart: any, cIndex: number) => (
																		<div key={cIndex}>
																			{cIndex === 0 ? <Form.Label>Initial amount</Form.Label> : null}
																			<InputGroup>
																				<Button type="button" size="sm" variant="outline-danger" onClick={() => arrayHelpers.remove(cIndex)}>✕</Button>
																				<CustomSelect
																					name={`species.[${index}]["annotation"]["cell4d:listOfValidCompartments"]["cell4d:compartment"].[${cIndex}][_attributes].id`}
																					value={values["species"][index]["annotation"]["cell4d:listOfValidCompartments"]["cell4d:compartment"][cIndex]._attributes.id}
																					defaultOptionString="compartment"
																					touched={touched}
																					errors={errors}
																					optionsArray={model.compartList}
																					onChange={handleChange}
																					onBlur={handleBlur} />
																				<Form.Control className="form-control" type="number" size="sm"
																					name={`species.[${index}]["annotation"]["cell4d:listOfValidCompartments"]["cell4d:compartment"].[${cIndex}][_attributes].initial`}
																					value={values["species"][index]["annotation"]["cell4d:listOfValidCompartments"]["cell4d:compartment"][cIndex]._attributes.initial}
																					onChange={handleChange}
																					onBlur={handleBlur}
																					isValid={getIn(touched, `species.[${index}]["annotation"]["cell4d:listOfValidCompartments"]["cell4d:compartment"].[${cIndex}][_attributes].initial`) &&
																						!getIn(errors, `species.[${index}]["annotation"]["cell4d:listOfValidCompartments"]["cell4d:compartment"].[${cIndex}][_attributes].initial`)}
																					isInvalid={getIn(touched, `species.[${index}]["annotation"]["cell4d:listOfValidCompartments"]["cell4d:compartment"].[${cIndex}][_attributes].initial`) &&
																						!!getIn(errors, `species.[${index}]["annotation"]["cell4d:listOfValidCompartments"]["cell4d:compartment"].[${cIndex}][_attributes].initial`)} />
																			</InputGroup>
																			<Row className="justify-content-center custom-error">
																				<Col style={{ paddingRight: 0 }} sm={2}></Col>
																				<Col style={{ paddingLeft: 0, paddingRight: 0 }}>
																					{getIn(touched, `species.[${index}]["annotation"]["cell4d:listOfValidCompartments"]["cell4d:compartment"].[${cIndex}][_attributes].id`) ?
																						errorsMap(getIn(errors, `species.[${index}]["annotation"]["cell4d:listOfValidCompartments"]["cell4d:compartment"].[${cIndex}][_attributes].id`))
																						: null
																					}
																				</Col>
																				<Col style={{ paddingLeft: 0 }}>
																					{getIn(touched, `species.[${index}]["annotation"]["cell4d:listOfValidCompartments"]["cell4d:compartment"].[${cIndex}][_attributes].initial`) ?
																						errorsMap(getIn(errors, `species.[${index}]["annotation"]["cell4d:listOfValidCompartments"]["cell4d:compartment"].[${cIndex}][_attributes].initial`))
																						: null
																					}
																				</Col>
																			</Row>

																			<br />
																		</div>
																	)) : null
															}
																<Button
																	type="button"
																	variant="outline-primary"
																	size="sm"
																	onClick={() => arrayHelpers.push(emptySpecies["annotation"]["cell4d:listOfValidCompartments"]["cell4d:compartment"][0])}
																	className="justify-content-flex-start">
																	Add compartment
																</Button>
															</div>
															)
														}}
														/>
													</Col>
													<Col xs={5} style={{ paddingRight: 30 }}>
														<FieldArray name={`species.[${index}]["annotation"]["cell4d:listOfSpeciesTypes"]["cell4d:speciesType"]`} render={arrayHelpers => {
															return (<div> {
																values?.["species"]?.[index]?.["annotation"]?.["cell4d:listOfSpeciesTypes"]?.["cell4d:speciesType"] && values?.["species"]?.[index]?.["annotation"]?.["cell4d:listOfSpeciesTypes"]?.["cell4d:speciesType"].length > 0 ?
																	values["species"][index]["annotation"]["cell4d:listOfSpeciesTypes"]["cell4d:speciesType"].map((annotSpecies: any, mIndex: number) => (
																		<div key={mIndex}>
																			{mIndex === 0 ? <Form.Label>Molecule species list</Form.Label> : null}
																			<Card border="light">
																				<Card.Body>
																					<Row style={{ marginTop: -10 }}>
																						<Col xs={6}>
																							<Form.Label>Base molecule</Form.Label>
																							<InputGroup>
																								<Button type="button" size="sm" variant="outline-danger" onClick={() => arrayHelpers.remove(mIndex)}>✕</Button>
																								<CustomSelect
																									name={`species.[${index}]["annotation"]["cell4d:listOfSpeciesTypes"]["cell4d:speciesType"].[${mIndex}][_attributes].id`}
																									value={values["species"][index]["annotation"]["cell4d:listOfSpeciesTypes"]["cell4d:speciesType"][mIndex]._attributes.id}
																									defaultOptionString="species"
																									touched={touched}
																									errors={errors}
																									errorPath={`species.[${index}]["annotation"]["cell4d:listOfSpeciesTypes"]["cell4d:speciesType"].[${mIndex}]`}
																									optionsArray={Object.keys(model.annotSpeciesList)}
																									onChange={(e) => {
																										handleChange(e);
																										setBindSiteState(!bindSiteState);
																										let blank_sites = model.annotSpeciesList[e.target.value].sites.map((site: { id: string, states: Array<string> }) => {
																											return ({
																												_attributes: {
																													id: site.id,
																													state: "",
																													binding: ""
																												}
																											})
																										})
																										setFieldValue(`species.[${index}]["annotation"]["cell4d:listOfSpeciesTypes"]["cell4d:speciesType"].[${mIndex}][cell4d:bindingSite]`, blank_sites);
																									}}
																									onBlur={handleBlur} />
																							</InputGroup>
																							<Row className="justify-content-center custom-error">
																								{getIn(touched, `species.[${index}]["annotation"]["cell4d:listOfSpeciesTypes"]["cell4d:speciesType"].[${mIndex}]`) ?
																									errorsMap(getIn(errors, `species.[${index}]["annotation"]["cell4d:listOfSpeciesTypes"]["cell4d:speciesType"].[${mIndex}][_attributes].id`))
																									: null
																								}
																							</Row>

																						</Col>
																						{/* show the states of the selected molecule */}
																						<Col xs={6}>
																							{values?.["species"]?.[index]?.["annotation"]?.["cell4d:listOfSpeciesTypes"]?.["cell4d:speciesType"]?.[mIndex]?._attributes?.id ?
																								<>
																									{/* Show the species sites if the site array exists and isn't empty */}
																									{values?.["species"]?.[index]?.["annotation"]?.["cell4d:listOfSpeciesTypes"]?.["cell4d:speciesType"]?.[mIndex]?.["cell4d:bindingSite"] &&
																										values?.["species"]?.[index]?.["annotation"]?.["cell4d:listOfSpeciesTypes"]?.["cell4d:speciesType"]?.[mIndex]?.["cell4d:bindingSite"].length > 0 ?
																										// map will create as many fields as binding sites on the molecule
																										values["species"][index]["annotation"]["cell4d:listOfSpeciesTypes"]["cell4d:speciesType"][mIndex]["cell4d:bindingSite"].map((site: any, siteIndex: number) => {
																											return (
																												<>
																													{/* Depending on if state info was filled, provide a different form */}
																													{site._attributes.id}
																													{model.annotSpeciesList[annotSpecies._attributes.id]?.sites?.[siteIndex]?.states?.[0] ?
																														<>
																															<CustomSelect
																																name={`species.[${index}]["annotation"]["cell4d:listOfSpeciesTypes"]["cell4d:speciesType"].[${mIndex}]["cell4d:bindingSite"][${siteIndex}][_attributes].state`}
																																value={values["species"][index]["annotation"]["cell4d:listOfSpeciesTypes"]["cell4d:speciesType"][mIndex]["cell4d:bindingSite"][siteIndex]._attributes.state}
																																defaultOptionString="select state"
																																touched={touched}
																																errors={errors}
																																errorPath={`species.[${index}]["annotation"]["cell4d:listOfSpeciesTypes"]["cell4d:speciesType"].[${mIndex}]`}
																																optionsArray={model.annotSpeciesList[annotSpecies._attributes.id].sites[siteIndex].states}
																																onChange={handleChange}
																																onBlur={handleBlur} />
																														</>
																														:
																														<>
																															<CustomSelect
																																name={`species.[${index}]["annotation"]["cell4d:listOfSpeciesTypes"]["cell4d:speciesType"].[${mIndex}]["cell4d:bindingSite"][${siteIndex}][_attributes].binding`}
																																value={values["species"][index]["annotation"]["cell4d:listOfSpeciesTypes"]["cell4d:speciesType"][mIndex]["cell4d:bindingSite"][siteIndex]._attributes.binding}
																																defaultOptionString="select binding"
																																touched={touched}
																																errors={errors}
																																errorPath={`species.[${index}]["annotation"]["cell4d:listOfSpeciesTypes"]["cell4d:speciesType"].[${mIndex}]`}
																																optionsArray={["unbound", "bound"]}
																																onChange={handleChange}
																																onBlur={handleBlur} />
																														</>
																													}
																												</>
																											)
																										})
																										: null}
																								</>
																								: null}
																						</Col>
																					</Row>
																					<Row className="justify-content-center custom-error">
																						{getIn(touched, `species.[${index}]["annotation"]["cell4d:listOfSpeciesTypes"]["cell4d:speciesType"].[${mIndex}]`) ?
																							errorsMap(getIn(errors, `species.[${index}]["annotation"]["cell4d:listOfSpeciesTypes"]["cell4d:speciesType"].[${mIndex}]`))
																							: null
																						}
																					</Row>
																				</Card.Body>
																			</Card>
																			<br />
																		</div>
																	)) : null
															}
																<Button
																	type="button"
																	size="sm"
																	variant="outline-primary"
																	onClick={() => arrayHelpers.push(empty_speciesType)}
																	className="justify-content-flex-start">
																	Add base species
																</Button>
																<br />
																<br />
															</div>
															)
														}}
														/>
													</Col>
												</Row>
											</Card>
											<Button type="button" variant="outline-danger" onClick={() => arrayHelpers.remove(index)}>Remove molecule</Button>
											<br />
											<br />
										</div>
									)) : null
								}
									<Button type="button" onClick={() => arrayHelpers.push(emptySpecies)}>Add new molecule</Button>
								</div>
								)
							}}
							/>
							{/* <br /> */}
							<hr />

							<Button type="submit" variant="success" onClick={() => {setSubmitSuccess(false); setTouchedSubmit(true)}}>Save molecules to model</Button>
							{/* Response to submit, either success or failure */}
							{submitSuccess ? <><br /><br /><div className="alert-success col-lg justify-content-md-center">Species saved to model!</div></> : 
											 touchedSubmit ? <><br /><br /><div className="alert-danger col-lg justify-content-md-center">Species failed to save.</div></> :
											 null}
							{/* <Row className="text-left">
								Errors
								<pre>{JSON.stringify(errors, null, 2)}</pre>
							</Row>
							<Row className="text-left">
								Touched
								<pre>{JSON.stringify(touched, null, 2)}</pre>
							</Row> */}

						</Container>
					</Form>
				)
				}
			</Formik>
		</div >
	);
}
